// evKit.tsx — shared Events-palette primitives for the volunteer loop surfaces
// (the rebuilt Roster sub-tabs and the Volunteers section). Ported verbatim
// from the approved prototype (events-live/manager-shell.jsx + manager-roster.jsx)
// so the look matches pixel-for-pixel: inline styles with the exact EV hexes
// rather than Tailwind, matching the prototype contract.

import type { CSSProperties, ReactNode } from 'react'
import { EvIcon as Icon } from './EvIcon'
import type { LibraryRole } from '../../types'

// Events palette — verbatim from events-live/tokens.jsx (EV) + the live green.
export const EV = {
  parchment: '#f5f1ea', parchmentDk: '#ece8df',
  ink: '#1a1a1a', inkSoft: '#5a5a5a',
  green: '#0a4a3a', greenSoft: '#dcebe5', greenDk: '#073629',
  brass: '#9b7f2a', brassSoft: '#f5edd5', brassBright: '#c9a13a',
  oxblood: '#7a2329', oxbloodSoft: '#f8dbdd',
  slate: '#3d5a6c', slateSoft: '#e0e7ec',
  amber: '#c98a4e', amberSoft: '#faeadb',
  line: '#d8d2c4',
  live: '#137a52',
  serif: "'Playfair Display',Georgia,serif",
  body: "'Source Sans 3',system-ui,sans-serif",
  mono: "'IBM Plex Mono',ui-monospace,monospace",
}

export type ToneKey = 'sage' | 'green' | 'slate' | 'brass' | 'heather' | 'amber'

// Role colour lanes (Events palette) — verbatim from manager-shell.jsx.
export const ROLE_TONES: Record<ToneKey, { bg: string; fg: string; dot: string }> = {
  sage:    { bg: '#dcebe5', fg: '#0a4a3a', dot: '#0a4a3a' },
  green:   { bg: '#d6e8df', fg: '#0a4a3a', dot: '#137a52' },
  slate:   { bg: '#e0e7ec', fg: '#3d5a6c', dot: '#3d5a6c' },
  brass:   { bg: '#f5edd5', fg: '#7a630f', dot: '#9b7f2a' },
  heather: { bg: '#ece3f0', fg: '#6b4e7a', dot: '#6b4e7a' },
  amber:   { bg: '#faeadb', fg: '#9a5a1e', dot: '#c98a4e' },
}

const TONE_KEYS: ToneKey[] = ['sage', 'slate', 'brass', 'heather', 'amber', 'green']

// The prototype assigned each role a fixed `tone`; real Roles have no tone
// column, so derive a stable one from the slug — same role always gets the
// same colour, with good spread across the six lanes.
export function toneForRole(role: { slug?: string | null; id: number }): ToneKey {
  const key = role.slug ?? String(role.id)
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return TONE_KEYS[h % TONE_KEYS.length]!
}

export function toneOf(role: LibraryRole | undefined) {
  return role ? ROLE_TONES[toneForRole(role)] : ROLE_TONES.slate
}

// ---- date + name helpers (mirror events-live/tokens.jsx) ------------------
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WD_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function isoDate(iso: string): Date { return new Date(iso + 'T00:00:00Z') }
export function wdShort(iso: string): string { return WD[isoDate(iso).getUTCDay()]! }
export function wdLong(iso: string): string { return WD_LONG[isoDate(iso).getUTCDay()]! }
export function dayNum(iso: string): number { return isoDate(iso).getUTCDate() }
export function moShort(iso: string): string { return MO[isoDate(iso).getUTCMonth()]! }
export function fmtDayChip(iso: string): string { return `${wdShort(iso)} ${dayNum(iso)} ${moShort(iso)}` }
export function fmtDateRange(days: string[]): string {
  if (!days.length) return ''
  const a = isoDate(days[0]!); const b = isoDate(days[days.length - 1]!)
  if (days.length === 1) return `${WD_LONG[a.getUTCDay()]!.slice(0, 3)} ${a.getUTCDate()} ${MO[a.getUTCMonth()]} ${a.getUTCFullYear()}`
  return `${a.getUTCDate()} ${MO[a.getUTCMonth()]} – ${b.getUTCDate()} ${MO[b.getUTCMonth()]} ${b.getUTCFullYear()}`
}
export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const p = name.trim().split(/\s+/)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1]![0] : '')).toUpperCase()
}
export function firstName(name: string | null | undefined): string { return (name || '').split(' ')[0] || '' }

// ---- primitives -----------------------------------------------------------
export function EvBadge({ children, tone = 'slate', solid = false, style }: {
  children: ReactNode; tone?: ToneKey; solid?: boolean; style?: CSSProperties
}) {
  const t = ROLE_TONES[tone] || ROLE_TONES.slate
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: EV.mono, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: solid ? '#fff' : t.fg, background: solid ? t.dot : t.bg, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', ...style }}>{children}</span>
  )
}

export function EvAvatar({ name, size = 34, tone = 'green', ring }: {
  name: string | null; size?: number; tone?: ToneKey; ring?: string
}) {
  const t = ROLE_TONES[tone] || ROLE_TONES.green
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', background: t.dot, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: EV.body, fontWeight: 700, fontSize: size * 0.38, flexShrink: 0, boxShadow: ring ? `0 0 0 2px ${EV.parchment}, 0 0 0 4px ${ring}` : 'none' }}>{initials(name)}</span>
  )
}

export function EvCard({ children, style, pad = 0 }: { children: ReactNode; style?: CSSProperties; pad?: number }) {
  return <div style={{ background: '#fff', border: `1px solid ${EV.line}`, borderRadius: 10, ...(pad ? { padding: pad } : {}), ...style }}>{children}</div>
}

export function StatusDot({ status }: { status: 'assigned' | 'confirmed' }) {
  const map = { assigned: { c: EV.amber, t: 'Pending confirm' }, confirmed: { c: EV.live, t: 'Confirmed' } }
  const m = map[status] || map.assigned
  return <span title={m.t} style={{ width: 8, height: 8, borderRadius: '50%', background: status === 'confirmed' ? m.c : '#fff', border: `2px solid ${m.c}`, flexShrink: 0 }} />
}

export function ProgressBar({ filled, need, height = 6 }: { filled: number; need: number; height?: number }) {
  const pct = need > 0 ? Math.min(100, Math.round((filled / need) * 100)) : 0
  const full = need > 0 && filled >= need
  return (
    <div style={{ height, background: EV.parchmentDk, borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: full ? EV.live : filled === 0 ? EV.oxblood : EV.amber, borderRadius: 999, transition: 'width .3s' }} />
    </div>
  )
}

export function SectionHead({ title, sub, count, style }: { title: string; sub?: string; count?: number; style?: CSSProperties }) {
  return (
    <div style={{ ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <h3 style={{ fontFamily: EV.serif, fontSize: 17, fontWeight: 700, margin: 0, color: EV.ink }}>{title}</h3>
        {count != null && <span style={{ fontFamily: EV.body, fontSize: 12, fontWeight: 800, color: '#fff', background: EV.green, borderRadius: 999, padding: '1px 9px' }}>{count}</span>}
      </div>
      {sub && <div style={{ fontFamily: EV.body, fontSize: 12.5, color: EV.inkSoft, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

export function EvEmpty({ icon, text }: { icon: string; text: string }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px dashed ${EV.line}`, borderRadius: 10, padding: '18px 20px', fontFamily: EV.body, fontSize: 13, color: EV.inkSoft }}><Icon name={icon} size={20} color={EV.inkSoft} /> {text}</div>
}

export function primaryBtn(): CSSProperties {
  return { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 8, border: 'none', background: EV.green, color: '#fff', fontFamily: EV.body, fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }
}
export function ghostBtn(): CSSProperties {
  return { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 13px', borderRadius: 8, border: `1px solid ${EV.line}`, background: '#fff', color: EV.inkSoft, fontFamily: EV.body, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }
}
